/**
 * "Switchback Vault" — original platformer level (difficulty 2/10).
 * Free movement, wall-stops instead of crash deaths, auto-checkpoints,
 * one-way climbs, a jetpack (Glider) flight section, and an endgate you
 * must physically reach. Completable moving only rightward (used by the
 * completability solver), but built to be explored.
 */
import type { LevelData, LevelObject } from '../../format/types';
import { UNITS_PER_BLOCK as B } from '../../engine/physics/constants';

let nextId = 1;
const objects: LevelObject[] = [];

function cell(bx: number, by: number): { x: number; y: number } {
  return { x: bx * B + B / 2, y: by * B + B / 2 };
}

function push(o: Omit<LevelObject, 'id'>): void {
  objects.push({ id: nextId++, ...o });
}

const spike = (bx: number, by = 0): void => push({ type: 'spike', ...cell(bx, by) });
const block = (bx: number, by = 0): void => push({ type: 'block', ...cell(bx, by) });
const slope = (bx: number, by: number, dir: 'up' | 'down', grade: 1 | 0.5 = 1): void =>
  push({ type: 'slope', ...cell(bx, by), props: { dir, grade } });
const oneway = (bx: number, by: number): void => push({ type: 'oneway', ...cell(bx, by) });
const portal = (bx: number, kind: string, by = 1): void =>
  push({ type: 'portal', ...cell(bx, by), props: { kind } });
const checkpoint = (bx: number, by = 1): void => push({ type: 'checkpoint', ...cell(bx, by) });
const ceiling = (bx0: number, bx1: number, by: number): void => {
  for (let bx = bx0; bx <= bx1; bx++) block(bx, by);
};

// --- Room 1: spike pits (0–40) ------------------------------------------------
spike(12);
spike(13);
spike(22);
spike(23);
checkpoint(30);

// --- Ramp & one-way climb (40–72) ----------------------------------------------
slope(44, 0, 'up');
block(45, 0);
block(46, 0);
block(47, 0);
slope(48, 0, 'down');
oneway(54, 1);
oneway(58, 2);
oneway(62, 3);
ceiling(66, 70, 3); // elevated walkway at the top of the climb
checkpoint(74);
spike(77);
spike(78);

// --- Jetpack flight (80–122) ----------------------------------------------------
portal(82, 'form:glider');
ceiling(84, 118, 6);
for (let bx = 88; bx <= 114; bx += 2) spike(bx);
portal(120, 'form:runner', 1);
portal(120, 'form:runner', 4);

// --- Wall hop, key & lock, finale (124–146) -----------------------------------
push({ type: 'collectible', ...cell(124, 1), props: { kind: 'key', itemId: 1 } });
block(126, 0);
block(126, 1); // platformer walls stop you — hop over
checkpoint(130);
spike(134);
spike(135);
// The vault door: three locked blocks that open with the key.
push({ type: 'lock', ...cell(138, 0), props: { itemId: 1, needed: 1 } });
push({ type: 'lock', ...cell(138, 1), props: { itemId: 1, needed: 1 } });
push({ type: 'lock', ...cell(138, 2), props: { itemId: 1, needed: 1 } });

// Secret shards in the vault's corners.
push({ type: 'collectible', ...cell(30, 2), props: { kind: 'shard' } });
push({ type: 'collectible', ...cell(58, 3), props: { kind: 'shard' } });
push({ type: 'collectible', ...cell(100, 4), props: { kind: 'shard' } });

push({ type: 'endgate', ...cell(143, 2) });

export const SWITCHBACK_VAULT: LevelData = {
  formatVersion: 1,
  meta: {
    name: 'Switchback Vault',
    author: 'Shape Shifters Team',
    description: 'Take your time. The vault is patient.',
    revision: 1,
    createdAt: '2026-07-23T00:00:00.000Z',
    updatedAt: '2026-07-23T00:00:00.000Z',
    difficulty: 2,
    tags: ['platformer', 'official'],
  },
  song: { id: 'undercurrent', bpm: 112, offsetMs: 0 },
  settings: {
    mode: 'platformer',
    speed: 'one',
    startForm: 'runner',
    accentColor: '#34d399',
    hazardColor: '#f43f5e',
    backgroundColor: '#04121f',
  },
  objects,
};
